/*
 * B2 — the Orbit discovery call, moved out of the consultant page header.
 *
 * The header had a Schedule call button that dialled the client's own number.
 * B2 moves delegation onto the section that is missing information, and the
 * per-section flow (CallModal on the problem page) does that properly. It
 * needs an engagement to scope the call to, though, and most clients do not
 * have one yet — a brand-new client is exactly who a consultant wants to ring.
 *
 * So this is the header's modal, relocated rather than deleted: the same
 * client-scoped call, reached from the block that has the gaps.
 */

import React, { useState } from 'react';
import { Loader2, Phone, X, Check } from 'lucide-react';

import { scheduleVoiceCall } from '../../services/api';

export interface DiscoveryCallModalProps {
  clientId: string;
  clientName: string;
  /** The block the caller wants filled, e.g. "Tools & stack". Copy only: this
   *  call covers the whole company picture, and saying otherwise would lie. */
  sectionLabel: string;
  defaultPhone?: string;
  onClose: () => void;
}

export const DiscoveryCallModal: React.FC<DiscoveryCallModalProps> = ({
  clientId, clientName, sectionLabel, defaultPhone = '', onClose,
}) => {
  const [phone, setPhone] = useState(defaultPhone);
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const go = async () => {
    if (!phone.trim()) {
      setErr('A phone number is needed to place the call.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // datetime-local gives local time with no zone; send it as an instant.
      const res = await scheduleVoiceCall(clientId, {
        toNumber: phone.trim(),
        scheduledAt: when ? new Date(when).toISOString() : undefined,
      });
      setDone(res.status);
    } catch (e: any) {
      setErr(e?.message || 'Could not place the call.');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-1 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-brand-600" />
            <h2 className="text-base font-bold text-gray-900">Orbit calls {clientName}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="py-3 text-center">
            <div className="mx-auto mb-2 grid h-10 w-10 place-items-center rounded-full bg-green-50">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-sm text-gray-700">
              {done === 'failed'
                ? 'Could not place the call. Check the number.'
                : when
                  ? 'Call scheduled. Orbit will dial at that time and fill the profile from what it hears.'
                  : 'Orbit is dialing now. It will fill the profile from what it hears.'}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-4 rounded-full bg-brand-500 px-4 py-2 text-sm font-semibold text-white"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <p className="mb-4 text-sm text-gray-500">
              Orbit researches the business, asks about {sectionLabel.toLowerCase()} and the rest of
              the picture, and fills the profile live. Leave the time empty to call now.
            </p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-[11px] font-sans uppercase tracking-wider text-gray-400">
                  Phone number
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-sans uppercase tracking-wider text-gray-400">
                  When <span className="normal-case tracking-normal text-gray-400">(empty means now)</span>
                </label>
                <input
                  type="datetime-local"
                  value={when}
                  onChange={e => setWhen(e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
            {err && <p className="mt-2 text-[12px] font-medium text-red-600">{err}</p>}
            <button
              type="button"
              onClick={go}
              disabled={busy}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50 whitespace-nowrap"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {when ? 'Schedule the call' : 'Call now'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DiscoveryCallModal;
